/**
 * Submission queue tab — NhanVienTrungTam side.
 *
 * Simplified flow:
 *   1. On mount, auto batch-accept all pending → accepted (teacher sees "đã tiếp nhận")
 *   2. NVTT sees accepted submissions, clicks to view detail panel
 *   3. From detail panel: "Vào khóa" or "Từ chối"
 *   4. entered / rejected tabs are read-only audit views
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import {
  listSubmissionQueueApi,
  batchAcceptSubmissionsApi,
  rejectSubmissionApi,
  enterSubmissionApi,
  deferSubmissionApi,
  undeferSubmissionApi,
  type TtSubmission,
  type SubmissionStatus,
  type SubmissionPayload,
} from '../services/trungTamApi';
import type { TtCourse } from '../types';
import ZoomableImage from './ZoomableImage';
import DvhcAutocomplete from './DvhcAutocomplete';
import ScanMethodBadge from './ScanMethodBadge';

type Props = {
  courses: TtCourse[];
  /**
   * The course selected in the parent left column. When set, popup auto-picks
   * this as the target course. If its hạng differs from the submission's,
   * the picker is flagged red and "Vào khóa" is disabled — NVTT must switch
   * to a same-hạng course before entering.
   */
  selectedCourseId?: number | null;
  onRecordCreated?: () => void;
};

type TabKey = 'accepted' | 'deferred' | 'entered' | 'rejected';
type SortKey = 'newest' | 'oldest' | 'name';

const getGivenName = (fullName: string): string => {
  const parts = fullName.trim().split(/\s+/);
  return parts[parts.length - 1] || fullName;
};

const STATUS_TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: 'accepted', label: 'Chờ nhập khóa', icon: 'inventory_2' },
  { key: 'deferred', label: 'Trì hoãn', icon: 'pause_circle' },
  { key: 'entered', label: 'Đã vào khóa', icon: 'check_circle' },
  { key: 'rejected', label: 'Đã từ chối', icon: 'cancel' },
];

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch {
    return '—';
  }
};

const thumbSrc = (sub: TtSubmission): string | null => {
  const src = sub.portraitBase64 || sub.avatarBase64;
  if (!src) return null;
  return src.startsWith('data:') ? src : `data:image/jpeg;base64,${src}`;
};

const imgSrc = (b64: string | null | undefined): string | null => {
  if (!b64) return null;
  return b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`;
};

// Extract a "cấp 2" short address: last two comma-separated segments
// (typically district + province). Falls back to the full string if the
// address has fewer parts.
const shortAddress = (full: string | null | undefined): string | null => {
  if (!full) return null;
  const parts = full.split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length <= 2) return parts.join(', ');
  return parts.slice(-2).join(', ');
};

const STATUS_CHIP: Record<SubmissionStatus, { label: string; color: string; icon: string }> = {
  pending: { label: 'Đang chờ', color: 'warn', icon: 'hourglass_top' },
  accepted: { label: 'Chờ nhập khóa', color: 'info', icon: 'inventory_2' },
  deferred: { label: 'Trì hoãn khóa sau', color: 'warn', icon: 'pause_circle' },
  entered: { label: 'Đã vào khóa', color: 'ok', icon: 'check_circle' },
  rejected: { label: 'Đã từ chối', color: 'fail', icon: 'cancel' },
};

const SubmissionQueueTab: React.FC<Props> = ({ courses, selectedCourseId: parentCourseId, onRecordCreated }) => {
  const [activeTab, setActiveTab] = useState<TabKey>('accepted');
  const [submissions, setSubmissions] = useState<TtSubmission[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailSub, setDetailSub] = useState<TtSubmission | null>(null);
  const [editedSub, setEditedSub] = useState<TtSubmission | null>(null);
  const [detailCourseId, setDetailCourseId] = useState<number | null>(null);
  const [detailMaDvhc, setDetailMaDvhc] = useState<string>('');
  const [detailDvhcText, setDetailDvhcText] = useState<string>('');
  const [initialMaDvhc, setInitialMaDvhc] = useState<string>('');
  const [initialDvhcText, setInitialDvhcText] = useState<string>('');
  const [rejectTarget, setRejectTarget] = useState<TtSubmission | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('newest');
  const autoAcceptedRef = useRef(false);

  // Sync editedSub + default course when a row is opened.
  // Preference order for the target course picker:
  //   1. Course currently selected in the parent left column (what NVTT is
  //      focused on) — this is the primary signal.
  //   2. Any other open course with the same hạng as the submission.
  //   3. null (user must pick manually).
  useEffect(() => {
    if (detailSub) {
      setEditedSub({ ...detailSub });
      const parent = parentCourseId
        ? courses.find(c => c.id === parentCourseId && c.status === 'open')
        : null;
      const sameHang = courses.find(c =>
        c.status === 'open' && (c.hang || '').toLowerCase() === detailSub.hang.toLowerCase(),
      );
      setDetailCourseId(parent?.id ?? sameHang?.id ?? null);
      // DVHC fields are persisted on the queue row by the teacher side but
      // aren't in the base TtSubmission type — read them defensively.
      const sub = detailSub as TtSubmission & { maDvhc?: string | null; dvhcTenDayDu?: string | null };
      const code = sub.maDvhc || '';
      const text = sub.dvhcTenDayDu || '';
      setDetailMaDvhc(code);
      setDetailDvhcText(text);
      setInitialMaDvhc(code);
      setInitialDvhcText(text);
    } else {
      setEditedSub(null);
      setDetailCourseId(null);
      setDetailMaDvhc('');
      setDetailDvhcText('');
      setInitialMaDvhc('');
      setInitialDvhcText('');
    }
  }, [detailSub, courses, parentCourseId]);

  const updateEditField = <K extends keyof TtSubmission>(field: K, value: TtSubmission[K]) => {
    setEditedSub(prev => (prev ? { ...prev, [field]: value } : null));
  };

  // Compute the subset of fields that differ from the original so we only
  // send overrides for what the NVTT actually changed.
  const buildOverrides = (orig: TtSubmission, edited: TtSubmission): Partial<SubmissionPayload> | undefined => {
    const keys: (keyof SubmissionPayload & keyof TtSubmission)[] = [
      'soCCCD', 'soCMT', 'hoTen', 'ngaySinh', 'gioiTinh',
      'diaChi', 'ngayCap', 'note', 'hang',
    ];
    const out: Partial<SubmissionPayload> = {};
    let changed = false;
    for (const k of keys) {
      if ((edited[k] ?? '') !== (orig[k] ?? '')) {
        (out as Record<string, unknown>)[k] = edited[k];
        changed = true;
      }
    }
    return changed ? out : undefined;
  };

  // All open courses — user must be able to see even a mismatched-hạng course
  // if the parent left column has it selected (we'll flag it red instead of
  // hiding it, so NVTT understands why "Vào khóa" is disabled).
  const openCourses = useMemo(
    () => courses.filter(c => c.status === 'open'),
    [courses],
  );

  // Hạng of the currently-selected target course vs the submission's hạng.
  const selectedCourse = useMemo(
    () => openCourses.find(c => c.id === detailCourseId) || null,
    [openCourses, detailCourseId],
  );
  const hangMismatch = !!(
    selectedCourse &&
    editedSub &&
    selectedCourse.hang &&
    selectedCourse.hang.toLowerCase() !== editedSub.hang.toLowerCase()
  );

  // Lock the queue to submissions of the currently-viewed course's hang.
  // Entering CCCD into a B-class course should NEVER show A or CE
  // submissions — prevents cross-hang mis-assignment by the operator.
  const parentCourseHang = useMemo(
    () => courses.find(c => c.id === parentCourseId)?.hang || null,
    [courses, parentCourseId],
  );

  // Hang-only count for the tab badge (ignores search/sort). Lets the
  // operator see "X hồ sơ cùng hạng" even while typing a filter query.
  const hangMatchedCount = useMemo(() => {
    if (!parentCourseHang) return submissions.length;
    const h = parentCourseHang.toLowerCase();
    return submissions.filter(s => (s.hang || '').toLowerCase() === h).length;
  }, [submissions, parentCourseHang]);

  const filtered = useMemo(() => {
    let list = [...submissions];
    if (parentCourseHang) {
      list = list.filter(s => (s.hang || '').toLowerCase() === parentCourseHang.toLowerCase());
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(s =>
        s.hoTen.toLowerCase().includes(q) ||
        (s.soCCCD || '').includes(q) ||
        (s.submitter?.username || '').toLowerCase().includes(q),
      );
    }
    if (sortBy === 'newest') {
      list.sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
    } else if (sortBy === 'oldest') {
      list.sort((a, b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime());
    } else if (sortBy === 'name') {
      list.sort((a, b) => getGivenName(a.hoTen).localeCompare(getGivenName(b.hoTen), 'vi'));
    }
    return list;
  }, [submissions, searchQuery, sortBy, parentCourseHang]);

  const refresh = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const resp = await listSubmissionQueueApi({ status: activeTab });
      if (resp.EC === 0 && Array.isArray(resp.DT)) {
        const next = resp.DT;
        setSubmissions(prev => {
          // Skip state update when nothing meaningful changed — prevents
          // the whole list from remounting every 20s (cause of the flicker).
          if (
            prev.length === next.length &&
            prev.every((p, i) =>
              p.id === next[i].id &&
              p.status === next[i].status &&
              p.faceMatchScore === next[i].faceMatchScore &&
              p.rejectionReason === next[i].rejectionReason,
            )
          ) {
            return prev;
          }
          return next;
        });
      } else if (!silent) {
        setSubmissions([]);
      }
    } catch {
      if (!silent) setSubmissions([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [activeTab]);

  // Auto batch-accept all pending on first mount
  useEffect(() => {
    if (autoAcceptedRef.current) return;
    autoAcceptedRef.current = true;
    batchAcceptSubmissionsApi().catch(() => {});
  }, []);

  useEffect(() => {
    refresh(false);
    const iv = setInterval(() => refresh(true), 20_000);
    return () => clearInterval(iv);
  }, [refresh]);

  const openRejectModal = (sub: TtSubmission) => {
    setRejectTarget(sub);
    setRejectReason('');
  };

  const submitReject = async () => {
    if (!rejectTarget || !rejectReason.trim()) {
      toast.warn('Vui lòng nhập lý do từ chối');
      return;
    }
    setActionBusy(true);
    try {
      const resp = await rejectSubmissionApi(rejectTarget.id, rejectReason.trim());
      if (resp.EC === 0) {
        toast.success('Đã từ chối hồ sơ');
        setRejectTarget(null);
        setDetailSub(null);
        await refresh(false);
      } else {
        toast.warn(resp.EM || 'Không từ chối được');
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { EM?: string } } };
      toast.error(e?.response?.data?.EM || 'Lỗi server');
    } finally {
      setActionBusy(false);
    }
  };

  const submitEnterFromDetail = async () => {
    if (!detailSub || !editedSub || !detailCourseId) {
      toast.warn('Vui lòng chọn khóa học đích');
      return;
    }
    if (!editedSub.hoTen?.trim()) {
      toast.warn('Họ tên không được để trống');
      return;
    }
    setActionBusy(true);
    try {
      const fieldOverrides = buildOverrides(detailSub, editedSub);
      // Include DVHC changes if NVTT picked a different admin unit
      type OverridesShape = Partial<SubmissionPayload> & { maDvhc?: string; dvhcTenDayDu?: string };
      const overrides: Record<string, unknown> = { ...(fieldOverrides || {}) };
      if (detailMaDvhc !== initialMaDvhc) overrides.maDvhc = detailMaDvhc || undefined;
      if (detailDvhcText !== initialDvhcText) overrides.dvhcTenDayDu = detailDvhcText || undefined;
      const resp = await enterSubmissionApi(editedSub.id, {
        courseId: detailCourseId,
        overrides: Object.keys(overrides).length > 0 ? (overrides as OverridesShape) : undefined,
      });
      if (resp.EC === 0) {
        toast.success(`Đã nhập ${editedSub.hoTen} vào khóa`);
        setDetailSub(null);
        await refresh(false);
        onRecordCreated?.();
      } else {
        toast.warn(resp.EM || 'Không nhập được');
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { EM?: string } } };
      toast.error(e?.response?.data?.EM || 'Lỗi server');
    } finally {
      setActionBusy(false);
    }
  };

  /** Push the currently-open detail submission into the "deferred" bucket. */
  const deferCurrent = async () => {
    if (!detailSub) return;
    if (!window.confirm(`Trì hoãn hồ sơ ${detailSub.hoTen} sang khóa sau cùng hạng?`)) return;
    setActionBusy(true);
    try {
      const resp = await deferSubmissionApi(detailSub.id);
      if (resp.EC === 0) {
        toast.success('Đã trì hoãn sang khóa sau');
        setDetailSub(null);
        await refresh(false);
      } else {
        toast.warn(resp.EM || 'Không trì hoãn được');
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { EM?: string } } };
      toast.error(e?.response?.data?.EM || 'Lỗi server');
    } finally {
      setActionBusy(false);
    }
  };

  /** Undo defer — push a deferred row back to 'accepted'. */
  const undeferCurrent = async () => {
    if (!detailSub) return;
    setActionBusy(true);
    try {
      const resp = await undeferSubmissionApi(detailSub.id);
      if (resp.EC === 0) {
        toast.success('Đã đưa hồ sơ trở lại hàng đợi');
        setDetailSub(null);
        await refresh(false);
      } else {
        toast.warn(resp.EM || 'Không thay đổi được');
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { EM?: string } } };
      toast.error(e?.response?.data?.EM || 'Lỗi server');
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div className="tt-queue">
      {/* Status tabs — no more "pending" tab */}
      <div className="tt-queue__tabs">
        {STATUS_TABS.map(tab => (
          <button
            key={tab.key}
            type="button"
            className={`tt-queue__tab ${activeTab === tab.key ? 'tt-queue__tab--active' : ''}`}
            onClick={() => { setActiveTab(tab.key); setDetailSub(null); }}
          >
            <i className="material-icons">{tab.icon}</i>
            {tab.label}
            {tab.key === 'accepted' && hangMatchedCount > 0 && activeTab === 'accepted' && (
              <span className="tt-queue__tab-badge">{hangMatchedCount}</span>
            )}
          </button>
        ))}
        <button
          type="button"
          className="tt-queue__refresh"
          onClick={() => { batchAcceptSubmissionsApi().then(() => refresh(false)).catch(() => refresh(false)); }}
          title="Làm mới + tiếp nhận hồ sơ mới"
        >
          <i className="material-icons">refresh</i>
        </button>
      </div>

      {/* Search + Sort bar */}
      <div className="tt-queue__toolbar">
        <div className="tt-queue__search">
          <i className="material-icons">search</i>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Tìm theo họ tên, CCCD, giáo viên..."
          />
          {searchQuery && (
            <button type="button" className="tt-queue__search-clear" onClick={() => setSearchQuery('')}>
              <i className="material-icons">close</i>
            </button>
          )}
        </div>
        <div className="tt-queue__sort">
          <select value={sortBy} onChange={e => setSortBy(e.target.value as SortKey)}>
            <option value="newest">Mới nhất trước</option>
            <option value="oldest">Cũ nhất trước</option>
            <option value="name">Theo tên (A-Z)</option>
          </select>
        </div>
        {searchQuery && <span className="tt-queue__count">{filtered.length} kết quả</span>}
      </div>

      {/* Queue list */}
      {loading ? (
        <div className="tt-queue__empty">Đang tải...</div>
      ) : filtered.length === 0 ? (
        <div className="tt-queue__empty">
          <i className="material-icons">inbox</i>
          <p>Không có hồ sơ ở trạng thái "{STATUS_TABS.find(t => t.key === activeTab)?.label}"</p>
        </div>
      ) : (
        <div className="tt-queue__layout">
          {/* Table list — detail opens in modal, no side panel */}
          <div className="tt-queue__list">
            <div className="tt-queue__wrap">
              <table className="tt-queue__table">
                <thead>
                  <tr>
                    <th style={{ width: 52 }}>STT</th>
                    <th></th>
                    <th>Họ tên / CCCD</th>
                    <th>Hạng</th>
                    <th>Giáo viên nộp</th>
                    <th>Ngày nộp</th>
                    {activeTab === 'entered' && <th>Khóa</th>}
                    {activeTab === 'rejected' && <th>Lý do</th>}
                    {activeTab === 'deferred' && <th>Trì hoãn từ</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(sub => (
                    <tr
                      key={sub.id}
                      className={`tt-queue__row ${detailSub?.id === sub.id ? 'tt-queue__row--selected' : ''}`}
                      onClick={() => setDetailSub(sub)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td><strong className="tt-queue__seqno">#{sub.seqNo}</strong></td>
                      <td>
                        {thumbSrc(sub) ? (
                          <img src={thumbSrc(sub)!} alt="" className="tt-queue__thumb" />
                        ) : (
                          <div className="tt-queue__thumb tt-queue__thumb--placeholder">
                            <i className="material-icons">person</i>
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="tt-queue__name">{sub.hoTen}</div>
                        <div className="tt-queue__cccd">{sub.soCCCD || '—'}</div>
                        <div className="tt-queue__row-meta">
                          <ScanMethodBadge method={sub.scanMethod} size="compact" />
                          {sub.faceMatchScore != null && (
                            <span className={`tt-queue__match tt-queue__match--${sub.faceMatchScore >= 80 ? 'ok' : sub.faceMatchScore >= 60 ? 'warn' : 'fail'}`}>
                              Khớp {sub.faceMatchScore}%
                            </span>
                          )}
                        </div>
                      </td>
                      <td><span className="tt-queue__hang">{sub.hang}</span></td>
                      <td>{sub.submitter?.username || `#${sub.submittedBy}`}</td>
                      <td className="tt-queue__dt">{formatDateTime(sub.submittedAt)}</td>
                      {activeTab === 'entered' && (
                        <td><span className="tt-queue__course-link">→ {sub.linkedCourse?.name || '—'}</span></td>
                      )}
                      {activeTab === 'rejected' && (
                        <td className="tt-queue__note">
                          <span className="tt-queue__reject">
                            <i className="material-icons">info</i>
                            {sub.rejectionReason || '—'}
                          </span>
                        </td>
                      )}
                      {activeTab === 'deferred' && (
                        <td className="tt-queue__dt">
                          {sub.deferredAt ? formatDateTime(sub.deferredAt) : '—'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

      {/* Detail modal — editable scanner-style layout with zoomable CCCD + portrait */}
      {detailSub && editedSub && (() => {
        const chip = STATUS_CHIP[detailSub.status] || STATUS_CHIP.accepted;
        const readOnly = detailSub.status !== 'accepted';
        return createPortal(
          <div className="tt-queue-modal__overlay" onClick={() => setDetailSub(null)}>
            <div className="tt-queue-modal tt-queue-modal--detail tt-queue-modal--edit" onClick={e => e.stopPropagation()}>
              {/* Header with avatar + status chip */}
              <div className="tt-queue-detail__header">
                <div className="tt-queue-detail__head-main">
                  {thumbSrc(detailSub) ? (
                    <img src={thumbSrc(detailSub)!} alt="" className="tt-queue-detail__avatar" />
                  ) : (
                    <div className="tt-queue-detail__avatar tt-queue-detail__avatar--placeholder">
                      <i className="material-icons">person</i>
                    </div>
                  )}
                  <div className="tt-queue-detail__head-text">
                    <h3>{editedSub.hoTen || '—'}</h3>
                    <div className="tt-queue-detail__chips">
                      <span className={`tt-queue-detail__chip tt-queue-detail__chip--${chip.color}`}>
                        <i className="material-icons">{chip.icon}</i>
                        {chip.label}
                      </span>
                      <ScanMethodBadge method={detailSub.scanMethod} />
                      <span className="tt-queue-detail__chip tt-queue-detail__chip--neutral">
                        <i className="material-icons">person</i>
                        {detailSub.submitter?.username || `#${detailSub.submittedBy}`}
                      </span>
                      <span className="tt-queue-detail__chip tt-queue-detail__chip--neutral">
                        <i className="material-icons">schedule</i>
                        {formatDateTime(detailSub.submittedAt)}
                      </span>
                      {detailSub.faceMatchScore != null && (
                        <span className={`tt-queue-detail__chip tt-queue-detail__chip--${detailSub.faceMatchScore >= 80 ? 'ok' : detailSub.faceMatchScore >= 60 ? 'warn' : 'fail'}`}>
                          <i className="material-icons">face_retouching_natural</i>
                          Khớp {detailSub.faceMatchScore}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="tt-queue__detail-close"
                  onClick={() => setDetailSub(null)}
                  aria-label="Đóng"
                >
                  <i className="material-icons">close</i>
                </button>
              </div>

              {/* 2-col body: left images (zoomable), right editable fields */}
              <div className="tt-queue-detail__body tt-queue-detail__body--edit">
                {/* Left: CCCD + portrait stacked */}
                <div className="tt-queue-detail__img-stack">
                  <div className="tt-queue-detail__img-col">
                    <label>
                      <i className="material-icons">badge</i>
                      Ảnh CCCD gốc
                    </label>
                    {imgSrc(editedSub.sourceImageBase64) ? (
                      <div className="tt-queue-detail__img-box">
                        <ZoomableImage src={imgSrc(editedSub.sourceImageBase64)!} alt="CCCD gốc" />
                      </div>
                    ) : imgSrc(editedSub.avatarBase64) ? (
                      <div className="tt-queue-detail__img-box">
                        <ZoomableImage src={imgSrc(editedSub.avatarBase64)!} alt="Avatar CCCD" />
                      </div>
                    ) : (
                      <div className="tt-queue-detail__img-box tt-queue-detail__img-box--empty">
                        <i className="material-icons">badge</i>
                        <span>Không có ảnh CCCD</span>
                      </div>
                    )}
                  </div>
                  <div className="tt-queue-detail__img-col">
                    <label>
                      <i className="material-icons">photo_camera</i>
                      Ảnh thẻ (chân dung)
                    </label>
                    {imgSrc(editedSub.portraitBase64) ? (
                      <div className="tt-queue-detail__img-box">
                        <ZoomableImage src={imgSrc(editedSub.portraitBase64)!} alt="Ảnh thẻ" />
                      </div>
                    ) : (
                      <div className="tt-queue-detail__img-box tt-queue-detail__img-box--empty">
                        <i className="material-icons">photo_camera</i>
                        <span>Không có ảnh thẻ</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: editable fields (scanner-form style) + course picker */}
                <div className="tt-queue-detail__fields">
                  <div className="tt-scanner__fields">
                    <div className="tt-scanner__field">
                      <i className="tt-scanner__field-icon material-icons">badge</i>
                      <div className="tt-scanner__field-content">
                        <div className="tt-scanner__field-label">CCCD</div>
                        <input
                          className="tt-scanner__field-input"
                          value={editedSub.soCCCD || ''}
                          onChange={e => updateEditField('soCCCD', e.target.value)}
                          readOnly={readOnly}
                        />
                      </div>
                    </div>
                    <div className="tt-scanner__field">
                      <i className="tt-scanner__field-icon material-icons">credit_card</i>
                      <div className="tt-scanner__field-content">
                        <div className="tt-scanner__field-label">CMT</div>
                        <input
                          className="tt-scanner__field-input"
                          value={editedSub.soCMT || ''}
                          onChange={e => updateEditField('soCMT', e.target.value)}
                          readOnly={readOnly}
                        />
                      </div>
                    </div>
                    <div className="tt-scanner__field tt-scanner__field--full">
                      <i className="tt-scanner__field-icon material-icons">person</i>
                      <div className="tt-scanner__field-content">
                        <div className="tt-scanner__field-label">Họ tên</div>
                        <input
                          className="tt-scanner__field-input"
                          value={editedSub.hoTen || ''}
                          onChange={e => updateEditField('hoTen', e.target.value)}
                          readOnly={readOnly}
                        />
                      </div>
                    </div>
                    <div className="tt-scanner__field">
                      <i className="tt-scanner__field-icon material-icons">cake</i>
                      <div className="tt-scanner__field-content">
                        <div className="tt-scanner__field-label">Ngày sinh</div>
                        <input
                          className="tt-scanner__field-input"
                          value={editedSub.ngaySinh || ''}
                          onChange={e => updateEditField('ngaySinh', e.target.value)}
                          readOnly={readOnly}
                        />
                      </div>
                    </div>
                    <div className="tt-scanner__field">
                      <i className="tt-scanner__field-icon material-icons">wc</i>
                      <div className="tt-scanner__field-content">
                        <div className="tt-scanner__field-label">Giới tính</div>
                        <input
                          className="tt-scanner__field-input"
                          value={editedSub.gioiTinh || ''}
                          onChange={e => updateEditField('gioiTinh', e.target.value)}
                          readOnly={readOnly}
                        />
                      </div>
                    </div>
                    <div className="tt-scanner__field">
                      <i className="tt-scanner__field-icon material-icons">event</i>
                      <div className="tt-scanner__field-content">
                        <div className="tt-scanner__field-label">Ngày cấp CCCD</div>
                        <input
                          className="tt-scanner__field-input"
                          value={editedSub.ngayCap || ''}
                          onChange={e => updateEditField('ngayCap', e.target.value)}
                          readOnly={readOnly}
                        />
                      </div>
                    </div>
                    <div className="tt-scanner__field">
                      <i className="tt-scanner__field-icon material-icons">class</i>
                      <div className="tt-scanner__field-content">
                        <div className="tt-scanner__field-label">Hạng</div>
                        <input
                          className="tt-scanner__field-input"
                          value={editedSub.hang || ''}
                          onChange={e => updateEditField('hang', e.target.value)}
                          readOnly={readOnly}
                        />
                      </div>
                    </div>
                    <div className="tt-scanner__field tt-scanner__field--full">
                      <i className="tt-scanner__field-icon material-icons">home</i>
                      <div className="tt-scanner__field-content">
                        <div className="tt-scanner__field-label">Địa chỉ (CCCD)</div>
                        <input
                          className="tt-scanner__field-input"
                          value={editedSub.diaChi || ''}
                          onChange={e => updateEditField('diaChi', e.target.value)}
                          readOnly={readOnly}
                        />
                      </div>
                    </div>
                    <div className="tt-scanner__field tt-scanner__field--full">
                      <i className="tt-scanner__field-icon material-icons">location_city</i>
                      <div className="tt-scanner__field-content">
                        <div className="tt-scanner__field-label">Đơn vị hành chính (cấp 2)</div>
                        {readOnly ? (
                          <input
                            className="tt-scanner__field-input"
                            value={detailDvhcText || '—'}
                            readOnly
                          />
                        ) : (
                          <DvhcAutocomplete
                            value={detailMaDvhc}
                            displayText={detailDvhcText}
                            onChange={(code, text) => {
                              setDetailMaDvhc(code);
                              setDetailDvhcText(text);
                            }}
                          />
                        )}
                      </div>
                    </div>
                    <div className="tt-scanner__field tt-scanner__field--full">
                      <i className="tt-scanner__field-icon material-icons">notes</i>
                      <div className="tt-scanner__field-content">
                        <div className="tt-scanner__field-label">Ghi chú</div>
                        <input
                          className="tt-scanner__field-input"
                          value={editedSub.note || ''}
                          onChange={e => updateEditField('note', e.target.value)}
                          readOnly={readOnly}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Course picker (only when accepted / still actionable) */}
                  {detailSub.status === 'accepted' && (
                    <div className={`tt-queue-detail__course-picker${hangMismatch ? ' tt-queue-detail__course-picker--mismatch' : ''}`}>
                      <label className="tt-scanner__field-label">
                        <i className="material-icons">school</i>
                        Khóa học đích (hồ sơ hạng <strong>{editedSub.hang}</strong>)
                      </label>
                      <select
                        className={`tt-scanner__field-input${hangMismatch ? ' tt-scanner__field-input--error' : ''}`}
                        value={detailCourseId ?? ''}
                        onChange={e => setDetailCourseId(e.target.value ? Number(e.target.value) : null)}
                      >
                        <option value="">— Chọn khóa —</option>
                        {openCourses.map(c => {
                          const mismatch = editedSub && c.hang && c.hang.toLowerCase() !== editedSub.hang.toLowerCase();
                          return (
                            <option key={c.id} value={c.id}>
                              {mismatch ? '⚠ ' : ''}{c.name} (Hạng {c.hang || '—'})
                            </option>
                          );
                        })}
                      </select>
                      {hangMismatch && (
                        <p className="tt-queue-modal__warn">
                          <i className="material-icons">error</i>
                          Khóa đang chọn <strong>hạng {selectedCourse?.hang}</strong>, khác với hồ sơ <strong>hạng {editedSub.hang}</strong>.
                          Hãy chọn khóa khác đúng hạng trước khi bấm "Vào khóa".
                        </p>
                      )}
                      {!hangMismatch && openCourses.filter(c => !c.hang || c.hang.toLowerCase() === editedSub.hang.toLowerCase()).length === 0 && (
                        <p className="tt-queue-modal__warn">
                          <i className="material-icons">warning</i>
                          Không có khóa đang mở hạng {editedSub.hang}. Hãy tạo khóa mới trước.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Read-only info for entered/rejected */}
                  {detailSub.status === 'entered' && detailSub.linkedCourse && (
                    <p className="tt-queue-detail__note tt-queue__course-link">
                      <i className="material-icons">check_circle</i>
                      Đã vào khóa: {detailSub.linkedCourse.name} (Hạng {detailSub.linkedCourse.hang})
                    </p>
                  )}
                  {detailSub.status === 'rejected' && detailSub.rejectionReason && (
                    <p className="tt-queue-detail__note tt-queue__reject">
                      <i className="material-icons">info</i>
                      Lý do từ chối: {detailSub.rejectionReason}
                    </p>
                  )}
                </div>
              </div>

              {/* Action buttons */}
              {detailSub.status === 'accepted' && (
                <div className="tt-queue-modal__actions tt-queue-detail__actions">
                  <button
                    type="button"
                    className="tt-btn tt-btn--outline tt-queue__reject-btn"
                    onClick={() => openRejectModal(editedSub)}
                    disabled={actionBusy}
                  >
                    <i className="material-icons">block</i> Từ chối
                  </button>
                  <button
                    type="button"
                    className="tt-btn tt-btn--outline"
                    onClick={deferCurrent}
                    disabled={actionBusy}
                    title="Chuyển hồ sơ sang khóa sau cùng hạng"
                  >
                    <i className="material-icons">pause_circle</i> Trì hoãn khóa sau
                  </button>
                  <button
                    type="button"
                    className="tt-btn tt-btn--success"
                    onClick={submitEnterFromDetail}
                    disabled={actionBusy || !detailCourseId || !editedSub.hoTen?.trim() || hangMismatch}
                    title={hangMismatch ? `Hạng hồ sơ (${editedSub.hang}) không khớp khóa đã chọn` : undefined}
                  >
                    <i className="material-icons">arrow_forward</i> Vào khóa
                  </button>
                </div>
              )}
              {detailSub.status === 'deferred' && (
                <div className="tt-queue-modal__actions tt-queue-detail__actions">
                  <button
                    type="button"
                    className="tt-btn tt-btn--outline"
                    onClick={undeferCurrent}
                    disabled={actionBusy}
                  >
                    <i className="material-icons">undo</i> Đưa lại hàng đợi
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body,
        );
      })()}

      {/* Reject modal */}
      {rejectTarget && createPortal(
        <div className="tt-queue-modal__overlay" onClick={() => setRejectTarget(null)}>
          <div className="tt-queue-modal" onClick={e => e.stopPropagation()}>
            <h3>Từ chối hồ sơ: {rejectTarget.hoTen}</h3>
            <p className="tt-queue-modal__hint">Giáo viên sẽ thấy lý do từ chối và có thể nộp lại hồ sơ mới.</p>
            <label>
              <span>Lý do <b className="tt-required">*</b></span>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="VD: Ảnh CCCD không rõ, thiếu ảnh thẻ, sai hạng..."
                maxLength={500}
                rows={4}
              />
            </label>
            <div className="tt-queue-modal__actions">
              <button type="button" className="tt-btn tt-btn--outline" onClick={() => setRejectTarget(null)} disabled={actionBusy}>
                Hủy
              </button>
              <button type="button" className="tt-btn tt-btn--danger" onClick={submitReject} disabled={actionBusy || !rejectReason.trim()}>
                Xác nhận từ chối
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Enter-into-course modal removed — course picker now lives inside detail modal */}
    </div>
  );
};

export default SubmissionQueueTab;
